import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, FileText,
  Eye, Users, CheckCircle2, TrendingUp, Loader2, Clock,
  RefreshCw, MousePointerClick,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { storiesApi, formsApi, analyticsApi } from '../api/client';
import CustomDomainsPanel from '../components/CustomDomainsPanel';
import { loadFontsFromStory } from '../lib/googleFonts';
import MiniPagePreview from '../components/story-dashboard/MiniPagePreview';
import API from '../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../utils/DefaultFunction';

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Story Preview Card — matches the story listing card style
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

function StoryPreviewCard({ story }) {
  const [pageIdx, setPageIdx] = useState(0);
  const pages = [...(story.pages || [])].sort((a, b) => a.pageIndex - b.pageIndex);
  const page = pages[pageIdx];

  useEffect(() => {
    const dur = page?.autoAdvanceDuration || 5000;
    const timer = setTimeout(() => {
      setPageIdx(i => (i < pages.length - 1 ? i + 1 : 0));
    }, dur);
    return () => clearTimeout(timer);
  }, [pageIdx, page, pages.length]);

  if (!page) return null;

  return (
    <div style={{
      height: 'calc(100vh - 220px)',
      maxHeight: '540px',
      aspectRatio: '9/17',
      position: 'relative',
      background: '#111',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    }}>
      {/* MiniPagePreview — same renderer used by the listing */}
      <div className="absolute inset-0">
        <MiniPagePreview background={page.background} layers={page.layers} />
      </div>

      {/* Progress bars — overlaid at top like a real story player */}
      {pages.length > 1 && (
        <div className="absolute top-0 inset-x-0 z-20 flex gap-0.5 px-2 pt-2">
          {pages.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 2.5, borderRadius: 99,
              background: i < pageIdx ? '#fff' : i === pageIdx ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>
      )}

      {/* Status badge */}
      <div className="absolute z-20" style={{ top: pages.length > 1 ? 16 : 8, left: 8 }}>
        <span style={{
          padding: '2px 6px', borderRadius: 99, fontSize: 9, fontWeight: 700,
          background: story.status === 'published' ? 'rgba(16,185,129,0.85)' : 'rgba(0,0,0,0.45)',
          color: '#fff', backdropFilter: 'blur(8px)',
        }}>
          {story.status === 'published' ? '● Live' : '○ Draft'}
        </span>
      </div>

      {/* Bottom gradient + title */}
      <div className="absolute bottom-0 inset-x-0 z-20 px-3 pb-3 pt-10"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.35) 60%, transparent 100%)' }}>
        <p className="truncate" style={{ color: '#fff', fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>
          {story.metadata?.title || story.title || 'Untitled Story'}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 9, marginTop: 2 }}>
          {pages.length} page{pages.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Click zones for tap-to-navigate */}
      <div className="absolute inset-0 flex z-10">
        <div className="w-1/3 h-full cursor-pointer" onClick={() => setPageIdx(i => Math.max(0, i - 1))} />
        <div className="w-1/3 h-full" />
        <div className="w-1/3 h-full cursor-pointer" onClick={() => setPageIdx(i => Math.min(pages.length - 1, i + 1))} />
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Main Story View Page
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

export default function StoryViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Story
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Permission gate — this page has no permission infrastructure of its own;
  // fetch the story-builder permission set so write actions (Edit Story) can be
  // gated without blocking the page itself.
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const permParams = "marketing/story-builder";

  useEffect(() => {
    const fetchPermissions = async () => {
      setPermissionsLoading(true);
      try {
        const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
        const response = await API.get(url, { headers: getApiHeaders() });
        setPermissions(response.data.data || []);
      } catch (error) {
        console.error("Error fetching permissions:", error);
        setPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };
    fetchPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canCreate = hasPermission("story-builder-create", permissions);
  // PERM-169: the route is gated by PermissionRoute (group="marketing/story-builder",
  // slug="story-builder-list"), but this page fetches its own permission set anyway
  // (for the Edit Story button above), so re-check the same slug here to also hold
  // off the story/analytics/forms fetches below until access is confirmed.
  const canView = hasPermission("story-builder-list", permissions);

  // Tabs
  const [activeTab, setActiveTab] = useState('analytics');

  // Analytics
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [expandedEvent, setExpandedEvent] = useState(null);

  // Interactive Analytics
  const [interactiveData, setInteractiveData] = useState(null);
  const [interactiveLoading, setInteractiveLoading] = useState(false);

  // Forms
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [submissions, setSubmissions] = useState({});
  const [submissionsLoading, setSubmissionsLoading] = useState({});

  // Load story
  useEffect(() => {
    if (permissionsLoading || !canView) return;
    if (!id) return;
    setLoading(true);
    storiesApi.get(id)
      .then(s => { loadFontsFromStory(s); setStory(s); setLoading(false); })
      .catch(() => { toast.error('Story not found'); navigate('/marketing/story-builder'); });
  }, [id, navigate, permissionsLoading, canView]);

  // Load analytics
  const loadAnalytics = useCallback(async (range) => {
    if (!id) return;
    setAnalyticsLoading(true);
    try {
      const fromDate = range === 'all' ? undefined
        : new Date(Date.now() - (range === '7d' ? 7 : range === '30d' ? 30 : 90) * 86400000).toISOString();
      const [statsData, eventsData] = await Promise.all([
        analyticsApi.getStats(id, fromDate),
        analyticsApi.getEvents({ story_id: id, limit: 20, page: 1 }),
      ]);
      setStats(statsData);
      setEvents(eventsData?.events || []);
      setEventsTotal(eventsData?.total || 0);
      setEventsPage(1);
    } catch {
      // Analytics may not be available
    }
    setAnalyticsLoading(false);
  }, [id]);

  useEffect(() => {
    if (permissionsLoading || !canView) return;
    loadAnalytics(dateRange);
  }, [dateRange, loadAnalytics, permissionsLoading, canView]);

  // Load interactive analytics (interactions + forms merged from ClickHouse + MongoDB)
  const loadInteractiveAnalytics = useCallback(async (range) => {
    if (!id) return;
    setInteractiveLoading(true);
    try {
      const fromDate = range === 'all' ? undefined
        : new Date(Date.now() - (range === '7d' ? 7 : range === '30d' ? 30 : 90) * 86400000).toISOString();
      const [statsData, detailsData] = await Promise.all([
        analyticsApi.getStats(id, fromDate),
        analyticsApi.getStoryDetails(id),
      ]);
      setInteractiveData({
        interactions: detailsData?.interactions || [],
        forms: (detailsData?.forms || []).map(f => {
          const chForm = (statsData?.formSubmits || []).find(s => s.formId === f.formId);
          return { ...f, submissionsInRange: chForm?.submissions ?? 0 };
        }),
      });
    } catch {
      // Analytics may not be available
    }
    setInteractiveLoading(false);
  }, [id]);

  useEffect(() => {
    if (permissionsLoading || !canView) return;
    if (activeTab === 'interactive') loadInteractiveAnalytics(dateRange);
  }, [activeTab, dateRange, loadInteractiveAnalytics, permissionsLoading, canView]);

  // Load forms — from API + from story layer data
  useEffect(() => {
    if (permissionsLoading || !canView) return;
    if (!id || !story) return;
    setFormsLoading(true);

    formsApi.list(id)
      .then(apiForms => {
        const allForms = [...(apiForms || [])];
        const existingIds = new Set(allForms.map(f => f.id));

        // Also discover forms embedded in story layers
        (story.pages || []).forEach(page => {
          (page.layers || []).forEach(layer => {
            const d = layer.data;
            if (d?.type === 'form' && d.formId && !existingIds.has(d.formId)) {
              existingIds.add(d.formId);
              allForms.push({
                id: d.formId,
                storyId: id,
                name: d.formTitle || 'Form',
                fields: d.fields || [],
                method: 'POST',
                actionUrl: '',
                successMessage: d.successMessage || '',
                errorMessage: d.errorMessage || '',
              });
            }
          });
        });

        setForms(allForms);
        setFormsLoading(false);
      })
      .catch(() => setFormsLoading(false));
  }, [id, story, permissionsLoading, canView]);

  // Load submissions for a form
  const loadSubmissions = useCallback(async (formId) => {
    if (!canView) return;
    setSubmissionsLoading(prev => ({ ...prev, [formId]: true }));
    try {
      const data = await formsApi.getSubmissions(formId);
      setSubmissions(prev => ({ ...prev, [formId]: data || [] }));
    } catch {
      toast.error('Failed to load submissions');
    }
    setSubmissionsLoading(prev => ({ ...prev, [formId]: false }));
  }, [canView]);

  // Auto-load all submissions when forms tab is active
  useEffect(() => {
    if (activeTab !== 'forms' || forms.length === 0) return;
    forms.forEach(form => {
      if (!submissions[form.id] && !submissionsLoading[form.id]) {
        loadSubmissions(form.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, forms, loadSubmissions]);

  // Load more events
  const loadMoreEvents = async (page) => {
    if (!id) return;
    try {
      const eventsData = await analyticsApi.getEvents({ story_id: id, limit: 20, page });
      setEvents(eventsData?.events || []);
      setEventsTotal(eventsData?.total || 0);
      setEventsPage(page);
    } catch { /* ignore */ }
  };

  // Format date
  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeAgo = (d) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(d);
  };

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fb' }}>
        <div className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto mb-3" style={{ color: 'var(--brand-primary)' }} />
          <p className="text-sm" style={{ color: '#94a3b8' }}>Loading story...</p>
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fb' }}>
        <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>
          You don't have permission to view this story.
        </p>
      </div>
    );
  }

  if (loading || !story) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fb' }}>
        <div className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto mb-3" style={{ color: 'var(--brand-primary)' }} />
          <p className="text-sm" style={{ color: '#94a3b8' }}>Loading story...</p>
        </div>
      </div>
    );
  }

  const totalEvents = eventsTotal;
  const totalPages = Math.ceil(totalEvents / 20);
  function getElementCount(arr, type) {
    let count = 0;

    for (const section of arr) {
      for (const layer of section.layers) {
        if (layer.data.type === type) {
          count++;
        }
      }
    }

    return count;
  }
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f8f9fb' }}>
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        <button onClick={() => navigate('/marketing/story-builder')} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={18} style={{ color: '#64748b' }} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold truncate" style={{ color: '#1e293b' }}>{story.metadata?.title || story.title || 'Untitled Story'}</h1>
          <p className="text-xs" style={{ color: '#94a3b8' }}>
            {story.status === 'published' ? 'Published' : 'Draft'} · {story.pages?.length || 0} pages
          </p>
        </div>
        {!permissionsLoading && canCreate && (
          <button
            onClick={() => navigate(`/marketing/story-builder/${id}/edit`)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{ background: 'var(--brand-primary)', color: '#ffffff' }}
          >
            Edit Story
          </button>
        )}
      </div>

      {/* ── Main Content ──────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Left Panel: Analytics & Forms ──────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ borderRight: '1px solid #e2e8f0', background: '#ffffff' }}>

          {/* Tab bar */}
          <div className="flex items-center px-5" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
            {([
              { key: 'analytics', label: 'Analytics', icon: BarChart3 ,identifier: 'analytics'},
              { key: 'interactive', label: 'Interactive Analytics', icon: MousePointerClick ,identifier: 'interactive'},
              { key: 'forms', label: 'Submitted Forms', icon: FileText ,identifier: 'form'},
              // { key: 'domains', label: 'Custom Domain', icon: Globe },
            ]).filter((item) => item.identifier === 'analytics' || getElementCount(story.pages || [], item.identifier) > 0).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-all"
                style={{
                  background: 'transparent',
                  color: activeTab === tab.key ? 'var(--brand-primary)' : '#94a3b8',
                  borderBottom: activeTab === tab.key ? '2px solid var(--brand-primary)' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto" style={{ background: '#f8fafc', padding: '20px' }}>

            {/* ══════════ ANALYTICS TAB ══════════ */}
            {activeTab === 'analytics' && (
              <div>
                {/* Date range filters */}
                <div className="flex items-center gap-2 mb-5">
                  {['7d', '30d', '90d', 'all'].map(range => (
                    <button
                      key={range}
                      onClick={() => setDateRange(range)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{
                        background: dateRange === range ? 'var(--brand-primary)' : '#ffffff',
                        color: dateRange === range ? '#ffffff' : '#64748b',
                        border: dateRange === range ? '1px solid var(--brand-primary)' : '1px solid #e2e8f0',
                      }}
                    >
                      {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : range === '90d' ? '90 Days' : 'All Time'}
                    </button>
                  ))}
                  <button onClick={() => loadAnalytics(dateRange)} className="ml-auto p-1.5 rounded-lg hover:bg-white transition-colors">
                    <RefreshCw size={14} style={{ color: '#94a3b8' }} />
                  </button>
                </div>

                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
                  </div>
                ) : !stats ? (
                  <div className="text-center py-16">
                    <BarChart3 size={40} className="mx-auto mb-3" style={{ color: '#e2e8f0' }} />
                    <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>No analytics data yet</p>
                    <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>Publish your story to start tracking views</p>
                  </div>
                ) : (
                  <>
                    {/* Stat cards */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {[
                        { label: 'Page Views', value: stats.pageViews || 0, icon: Eye, color: 'var(--brand-primary)', bg: 'var(--brand-50)' },
                        { label: 'Unique Viewers', value: stats.uniqueViewers || 0, icon: Users, color: '#10b981', bg: '#ecfdf5' },
                        { label: 'Completions', value: stats.storyCompletions || 0, icon: CheckCircle2, color: '#f59e0b', bg: '#fffbeb' },
                        { label: 'Completion Rate', value: `${(stats.completionRate || 0).toFixed(1)}%`, icon: TrendingUp, color: '#8b5cf6', bg: '#f5f3ff' },
                      ].map(card => (
                        <div key={card.label} className="rounded-xl p-4" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                              <card.icon size={15} style={{ color: card.color }} />
                            </div>
                            <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>{card.label}</span>
                          </div>
                          <p className="text-2xl font-bold" style={{ color: '#1e293b' }}>{card.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Event breakdown */}
                    {stats.eventBreakdown && stats.eventBreakdown.length > 0 && (
                      <div className="rounded-xl p-4 mb-4" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <h3 className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: '#94a3b8' }}>Event Breakdown</h3>
                        <div className="space-y-2.5">
                          {stats.eventBreakdown.map(ev => {
                            const maxCount = Math.max(...stats.eventBreakdown.map(e => e.count));
                            return (
                              <div key={ev.event} className="flex items-center gap-3">
                                <span className="text-xs font-medium w-32 truncate" style={{ color: '#64748b' }}>{ev.event}</span>
                                <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
                                  <div className="h-full rounded-full flex items-center px-2.5"
                                    style={{ width: `${Math.max(5, (ev.count / maxCount) * 100)}%`, background: 'linear-gradient(90deg, var(--brand-primary), #6c83f5)' }}>
                                    <span className="text-[10px] font-semibold text-white">{ev.count}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Page-by-page views */}
                    {stats.pageViewsByPage && stats.pageViewsByPage.length > 0 && (
                      <div className="rounded-xl p-4 mb-4" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <h3 className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: '#94a3b8' }}>Page Views Funnel</h3>
                        <div className="space-y-2">
                          {stats.pageViewsByPage.map((pv, i) => {
                            const maxViews = Math.max(...stats.pageViewsByPage.map(p => p.views));
                            const dropoff = i > 0 ? ((stats.pageViewsByPage[i - 1].views - pv.views) / stats.pageViewsByPage[i - 1].views * 100) : 0;
                            return (
                              <div key={pv.pageIndex} className="flex items-center gap-2">
                                <span className="text-xs w-16 text-right font-medium" style={{ color: '#94a3b8' }}>Page {Number(pv.pageIndex) + 1}</span>
                                <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
                                  <div className="h-full rounded-full flex items-center px-2"
                                    style={{ width: `${Math.max(5, (pv.views / maxViews) * 100)}%`, background: 'linear-gradient(90deg, #10b981, #34d399)' }}>
                                    <span className="text-[10px] font-semibold text-white">{pv.views}</span>
                                  </div>
                                </div>
                                {dropoff > 0 && (
                                  <span className="text-[10px] font-medium w-14 text-right" style={{ color: '#ef4444' }}>
                                    -{dropoff.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Event log */}
                    {events.length > 0 && (
                      <div className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <div className="px-4 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#94a3b8' }}>Event Log</h3>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>Event</th>
                              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>Time</th>
                              <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {events.map(ev => (
                              <tr key={ev._id} className="cursor-pointer transition-colors"
                                style={{ borderBottom: '1px solid #f8fafc' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                onClick={() => setExpandedEvent(expandedEvent === ev._id ? null : ev._id)}>
                                <td className="px-4 py-2.5">
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                    style={{ background: 'var(--brand-50)', color: 'var(--brand-primary)' }}>
                                    {ev.event}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 whitespace-nowrap" style={{ color: '#94a3b8' }}>{formatTimeAgo(ev.timestamp)}</td>
                                <td className="px-4 py-2.5">
                                  {expandedEvent === ev._id && ev.properties ? (
                                    <pre className="text-[10px] whitespace-pre-wrap" style={{ color: '#64748b' }}>
                                      {JSON.stringify(ev.properties, null, 2)}
                                    </pre>
                                  ) : (
                                    <span style={{ color: '#cbd5e1' }}>
                                      {ev.properties ? 'Click to expand' : '—'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 px-4 py-3" style={{ borderTop: '1px solid #f1f5f9' }}>
                            <button onClick={() => loadMoreEvents(eventsPage - 1)} disabled={eventsPage <= 1}
                              className="px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                              style={{ background: '#f1f5f9', color: eventsPage <= 1 ? '#cbd5e1' : '#64748b' }}>
                              Prev
                            </button>
                            <span className="text-xs" style={{ color: '#94a3b8' }}>Page {eventsPage} of {totalPages}</span>
                            <button onClick={() => loadMoreEvents(eventsPage + 1)} disabled={eventsPage >= totalPages}
                              className="px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                              style={{ background: '#f1f5f9', color: eventsPage >= totalPages ? '#cbd5e1' : '#64748b' }}>
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ══════════ INTERACTIVE ANALYTICS TAB ══════════ */}
            {activeTab === 'interactive' && (
              <div>
                {/* Date range + refresh */}
                <div className="flex items-center gap-2 mb-5">
                  {['7d', '30d', '90d', 'all'].map(range => (
                    <button
                      key={range}
                      onClick={() => setDateRange(range)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{
                        background: dateRange === range ? 'var(--brand-primary)' : '#ffffff',
                        color: dateRange === range ? '#ffffff' : '#64748b',
                        border: dateRange === range ? '1px solid var(--brand-primary)' : '1px solid #e2e8f0',
                      }}
                    >
                      {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : range === '90d' ? '90 Days' : 'All Time'}
                    </button>
                  ))}
                  <button onClick={() => loadInteractiveAnalytics(dateRange)} className="ml-auto p-1.5 rounded-lg hover:bg-white transition-colors">
                    <RefreshCw size={14} style={{ color: '#94a3b8' }} />
                  </button>
                </div>

                {interactiveLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
                  </div>
                ) : (() => {
                  // Filter out 'results' type — it is a display-only slide with no option data
                  const visibleInteractions = (interactiveData?.interactions || [])
                    .filter(i => i.interactiveType !== 'results');
                  const visibleForms = interactiveData?.forms || [];
                  const isEmpty = visibleInteractions.length === 0 && visibleForms.length === 0;

                  if (isEmpty) return (
                    <div className="text-center py-16">
                      <MousePointerClick size={40} className="mx-auto mb-3" style={{ color: '#e2e8f0' }} />
                      <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>No interactive elements yet</p>
                      <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>Add polls, quizzes, or forms to your story to see analytics here</p>
                    </div>
                  );

                  const COLORS = ['var(--brand-primary)', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

                  const typeLabel = {
                    'binary-poll': 'Poll',
                    'text-poll': 'Poll',
                    'text-quiz': 'Quiz',
                    'slider': 'Slider',
                    'emoji-slider': 'Slider',
                    'rating': 'Rating',
                  };

                  const typeBg = {
                    'binary-poll': { bg: 'var(--brand-50)', color: 'var(--brand-primary)' },
                    'text-poll': { bg: 'var(--brand-50)', color: 'var(--brand-primary)' },
                    'text-quiz': { bg: '#fef3c7', color: '#d97706' },
                    'slider': { bg: '#f0fdf4', color: '#16a34a' },
                    'emoji-slider': { bg: '#f0fdf4', color: '#16a34a' },
                    'rating': { bg: '#fff7ed', color: '#ea580c' },
                  };

                  return (
                    <div className="space-y-4">

                      {/* ── Interactions ── */}
                      {visibleInteractions.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: '#94a3b8' }}>
                            <MousePointerClick size={12} style={{ color: 'var(--brand-primary)' }} />
                            Interactions · {visibleInteractions.length}
                          </p>
                          <div className="space-y-3">
                            {visibleInteractions.map((interaction, idx) => {
                              const isSlider = interaction.interactiveType === 'slider' || interaction.interactiveType === 'emoji-slider';
                              const responses = interaction.totalResponses ?? 0;
                              const badge = typeBg[interaction.interactiveType] || { bg: '#f1f5f9', color: '#64748b' };

                              return (
                                <div key={interaction.interactionId || idx} className="rounded-xl p-4" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>

                                  {/* Card header */}
                                  <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1.5">
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                                          style={{ background: badge.bg, color: badge.color }}>
                                          {typeLabel[interaction.interactiveType] || interaction.interactiveType}
                                        </span>
                                        <span className="text-[10px]" style={{ color: '#94a3b8' }}>
                                          {interaction.pageName || `Page ${(interaction.pageIndex ?? 0) + 1}`}
                                        </span>
                                      </div>
                                      <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                                        {interaction.prompt || 'Interaction'}
                                      </p>
                                    </div>
                                    <div className="ml-4 shrink-0 text-right px-3 py-1.5 rounded-lg" style={{ background: '#f8fafc' }}>
                                      <p className="text-lg font-bold" style={{ color: '#1e293b' }}>{responses.toLocaleString()}</p>
                                      <p className="text-[10px]" style={{ color: '#94a3b8' }}>response{responses !== 1 ? 's' : ''}</p>
                                    </div>
                                  </div>

                                  {/* Slider: show average gauge */}
                                  {isSlider && responses > 0 && (
                                    <div>
                                      <div className="flex items-center justify-between text-xs mb-2" style={{ color: '#94a3b8' }}>
                                        <span>0</span>
                                        <span className="font-semibold" style={{ color: '#1e293b' }}>
                                          avg {interaction.average ?? 0}
                                        </span>
                                        <span>100</span>
                                      </div>
                                      <div className="h-3 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
                                        <div className="h-full rounded-full" style={{ width: `${interaction.average ?? 0}%`, background: 'linear-gradient(90deg, #10b981, #34d399)' }} />
                                      </div>
                                      {interaction.min !== undefined && (
                                        <div className="flex gap-3 mt-3">
                                          {[['Min', interaction.min], ['Avg', interaction.average], ['Max', interaction.max]].map(([label, val]) => (
                                            <div key={label} className="text-center px-3 py-1.5 rounded-lg" style={{ background: '#f8fafc' }}>
                                              <p className="text-sm font-bold" style={{ color: '#1e293b' }}>{val}</p>
                                              <p className="text-[10px]" style={{ color: '#94a3b8' }}>{label}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Poll / Quiz: option bars */}
                                  {!isSlider && (interaction.options || []).length > 0 && (
                                    responses === 0 ? (
                                      <p className="text-xs" style={{ color: '#cbd5e1' }}>No responses yet</p>
                                    ) : (
                                      <div className="space-y-2">
                                        {interaction.options.map((opt, oi) => {
                                          const pct = opt.percentage ?? 0;
                                          const isQuiz = interaction.interactiveType === 'text-quiz';
                                          const color = isQuiz
                                            ? (opt.isCorrect ? '#16a34a' : '#ef4444')
                                            : COLORS[oi % COLORS.length];
                                          return (
                                            <div key={opt.id || oi} className="flex items-center gap-2">
                                              {isQuiz && (
                                                <span className="text-[11px] w-3 shrink-0 text-center font-bold" style={{ color }}>
                                                  {opt.isCorrect ? '✓' : '✗'}
                                                </span>
                                              )}
                                              <span className="text-xs w-20 truncate shrink-0" style={{ color: '#64748b' }}>
                                                {opt.text || `Option ${oi + 1}`}
                                              </span>
                                              <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
                                                <div className="h-full rounded-full flex items-center px-2.5"
                                                  style={{ width: `${Math.max(pct > 0 ? 4 : 0, pct)}%`, background: color }}>
                                                  {opt.count > 0 && (
                                                    <span className="text-[10px] font-semibold text-white">{opt.count}</span>
                                                  )}
                                                </div>
                                              </div>
                                              <span className="text-xs w-9 text-right shrink-0 font-semibold" style={{ color: '#64748b' }}>
                                                {pct.toFixed(0)}%
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )
                                  )}

                                  {!isSlider && responses === 0 && (
                                    <p className="text-xs" style={{ color: '#cbd5e1' }}>No responses yet</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* ── Form Submissions ── */}
                      {visibleForms.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: '#94a3b8' }}>
                            <FileText size={12} style={{ color: 'var(--brand-primary)' }} />
                            Form Submissions · {visibleForms.length}
                          </p>
                          <div className="space-y-3">
                            {visibleForms.map((form, idx) => {
                              const inRange = form.submissionsInRange ?? 0;
                              const allTime = form.totalSubmissions ?? 0;
                              const pct = allTime > 0 ? Math.min(100, (inRange / allTime) * 100) : 0;
                              return (
                                <div key={form.formId || idx} className="rounded-xl p-4" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--brand-50)' }}>
                                      <FileText size={15} style={{ color: 'var(--brand-primary)' }} />
                                    </div>
                                    <p className="text-sm font-semibold flex-1 min-w-0 truncate" style={{ color: '#1e293b' }}>
                                      {form.name || form.formTitle || 'Untitled Form'}
                                    </p>
                                  </div>
                                  <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: '#f1f5f9' }}>
                                    <div className="h-full rounded-full" style={{ width: `${Math.max(pct > 0 ? 2 : 0, pct)}%`, background: 'linear-gradient(90deg, var(--brand-primary), #6c83f5)' }} />
                                  </div>
                                  <div className="flex gap-3">
                                    <div className="px-3 py-1.5 rounded-lg" style={{ background: 'var(--brand-50)' }}>
                                      <p className="text-base font-bold" style={{ color: 'var(--brand-primary)' }}>{inRange.toLocaleString()}</p>
                                      <p className="text-[10px]" style={{ color: '#94a3b8' }}>in period</p>
                                    </div>
                                    <div className="px-3 py-1.5 rounded-lg" style={{ background: '#f8fafc' }}>
                                      <p className="text-base font-bold" style={{ color: '#1e293b' }}>{allTime.toLocaleString()}</p>
                                      <p className="text-[10px]" style={{ color: '#94a3b8' }}>all time</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                    </div>
                  );
                })()}
              </div>
            )}

            {/* ══════════ CUSTOM DOMAIN TAB ══════════ */}
            {activeTab === 'domains' && (
              <CustomDomainsPanel
                tenantId={story?.metadata?.tenantId || story?.tenantId || 'my-tenant'}
              />
            )}

            {/* ══════════ SUBMITTED FORMS TAB ══════════ */}
            {activeTab === 'forms' && (
              <div>
                {formsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
                  </div>
                ) : forms.length === 0 ? (
                  <div className="text-center py-16">
                    <FileText size={40} className="mx-auto mb-3" style={{ color: '#e2e8f0' }} />
                    <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>No forms in this story</p>
                    <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>Add a form element in the editor to collect submissions</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {forms.map(form => {
                      const formSubmissions = submissions[form.id] || [];
                      const isLoading = submissionsLoading[form.id];
                      const columns = form.fields?.length > 0
                        ? form.fields.map(f => ({ key: f.name || f.id, label: f.label || f.name || f.id }))
                        : formSubmissions.length > 0
                          ? Object.keys(formSubmissions[0].data || {}).map(key => ({ key, label: key }))
                          : [];

                      return (
                        <div key={form.id} className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                          {/* Form header */}
                          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--brand-50)' }}>
                                <FileText size={13} style={{ color: 'var(--brand-primary)' }} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>{form.name || 'Untitled Form'}</p>
                                <p className="text-xs" style={{ color: '#94a3b8' }}>
                                  {form.fields?.length || 0} fields
                                  {!isLoading && submissions[form.id] !== undefined &&
                                    ` · ${formSubmissions.length} submission${formSubmissions.length !== 1 ? 's' : ''}`}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => loadSubmissions(form.id)}
                              className="p-1.5 rounded-lg transition-colors" style={{ background: '#f8fafc' }}>
                              <RefreshCw size={13} style={{ color: '#94a3b8' }} />
                            </button>
                          </div>

                          {/* Submissions table */}
                          {isLoading ? (
                            <div className="flex items-center justify-center py-10">
                              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
                            </div>
                          ) : submissions[form.id] === undefined ? (
                            <div className="flex items-center justify-center py-10">
                              <p className="text-xs" style={{ color: '#cbd5e1' }}>Loading…</p>
                            </div>
                          ) : formSubmissions.length === 0 ? (
                            <div className="flex items-center justify-center py-10">
                              <p className="text-xs" style={{ color: '#cbd5e1' }}>No submissions yet</p>
                            </div>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr style={{ background: '#f8fafc' }}>
                                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap" style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>#</th>
                                    {columns.map(col => (
                                      <th key={col.key} className="text-left px-4 py-2.5 font-semibold whitespace-nowrap capitalize" style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                                        {col.label}
                                      </th>
                                    ))}
                                    <th className="text-left px-4 py-2.5 font-semibold whitespace-nowrap" style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                                      <div className="flex items-center gap-1"><Clock size={10} /> Time</div>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {formSubmissions.map((sub, idx) => (
                                    <tr key={sub._id} style={{ borderBottom: idx < formSubmissions.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                                      <td className="px-4 py-2.5">
                                        <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold"
                                          style={{ background: 'var(--brand-50)', color: 'var(--brand-primary)' }}>
                                          {idx + 1}
                                        </span>
                                      </td>
                                      {columns.map(col => (
                                        <td key={col.key} className="px-4 py-2.5" style={{ color: '#1e293b' }}>
                                          {sub.data?.[col.key] || '—'}
                                        </td>
                                      ))}
                                      <td className="px-4 py-2.5 whitespace-nowrap" style={{ color: '#94a3b8' }}>
                                        {formatTimeAgo(sub.submittedAt)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Right Panel: Story Preview ────────────────────── */}
        <div style={{
          width: '40%', minWidth: '40%',
          background: '#f8f9fb',
          borderLeft: '1px solid #e2e8f0',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', padding: '16px',
        }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: '#94a3b8' }}>
            Preview
          </p>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
            <StoryPreviewCard story={story} />
          </div>
        </div>
      </div>
    </div>
  );
}
